import _ = require('underscore');
import { delayPromise } from '~commonInfra/utils/Delay';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { canSubmitLabel } from '~src/catalog/components/labels/canSubmitLabel';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/labels/DataQualityType';
import { JsonRestApiRequestConfig, makeJsonRestApiRequest } from '~src/httpClient/RestApiService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ERROR_NOTIFYING_SERVER_RESPONSE_DELAY } from '~src/httpClient/WebHttpClientWithErrorToast';

import { ContentTypesWhichCanBeCertified, DataQualityCertification, DataQualityCertificationContentListItem } from './DataQualityIndicator';
import { deleteDataQualityCertificationsForItems } from './deleteDataQualityCertificationsForItem';

const setDataQualityCertificationConfig: JsonRestApiRequestConfig = {
  restApiVersion: '3.13',
  serverWideRequest: false,
  httpMethod: 'POST',
  endpoint: 'dataQualityCertifications'
};

type SetDataQualityCertificationBody = {
  readonly contentList: {
    readonly content: ReadonlyArray<DataQualityCertificationContentListItem>;
  };
  readonly dataQualityIndicator: DataQualityCertification;
}

type SetDataQualityCertificationParams = {
  readonly id: string;
  readonly luid?: string;
  readonly isCertified: boolean;
  readonly certificationNote: string;
}

export async function updateOrDeleteCertificationForItem(params: SetDataQualityCertificationParams & { contentType: ContentTypesWhichCanBeCertified }): Promise<IResult> {
  const command = canSubmitLabel({ active: params.isCertified, message: params.certificationNote });

  switch (command) {
    case 'DELETE':
      return deleteCertificationForItem(params);
    case 'SUBMIT':
      return updateCertificationForItem(params);
    case 'TOOLONG':
      throw new Error('The certification message was too long to submit to the server');
    case 'NOMESSAGE':
      throw new Error('A message is required for a certification to be submitted');
    default:
      assertUnreachable(command);
  }
}

function mapUpdateParamsToBody(params: SetDataQualityCertificationParams & { contentType: ContentTypesWhichCanBeCertified }): SetDataQualityCertificationBody {
  const item = { id: params.luid, contentType: params.contentType } as DataQualityCertificationContentListItem;
  return {
    dataQualityIndicator: {
      type: CERTIFIED_INDICATOR_TYPE,
      message: params.certificationNote,
      active: params.isCertified,
      elevated: true,
    },
    contentList: { content: [item] }
  };
}

async function updateCertificationForItem(params: SetDataQualityCertificationParams & { contentType: ContentTypesWhichCanBeCertified }): Promise<IResult> {
  const result = await makeJsonRestApiRequest<SetDataQualityCertificationBody, {}>({
    config: setDataQualityCertificationConfig,
    request: mapUpdateParamsToBody({ ...params, contentType: params.contentType })
  });

  await delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
  return result;
}

async function deleteCertificationForItem(params: SetDataQualityCertificationParams & { contentType: ContentTypesWhichCanBeCertified }): Promise<IResult> {
  return deleteDataQualityCertificationsForItems({
    items: [{
      contentLuid: params.luid,
      contentType: params.contentType,
    }]
  });
}
